/** @file Paints one curve-graph frame on a Canvas2D context: grid, curves, handles, keys, playhead, box. */
import { normalizeRect } from '@pose2d/editor-geometry/normalizeRect';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { CurveCanvas, CurveFrame, CurveStyle } from './curveCanvasTypes';
import { CURVE_GRAPH } from './curveGraph.data';
import { CURVE_POINT } from './curvePoint.data';
import { drawCurveGrid } from './drawCurveGrid';

const colour = (style: CurveStyle, track: number): string => style.palette[track % Math.max(1, style.palette.length)] ?? style.text;

/** Strokes x, y pairs as one path; a NaN pair lifts the pen. */
function strokeLine(ctx: CurveCanvas, line: readonly number[]): void {
  ctx.beginPath();
  let pen = false;
  for (let i = 0; i + 1 < line.length; i += 2) {
    const x = line[i] ?? Number.NaN;
    const y = line[i + 1] ?? Number.NaN;
    if (Number.isNaN(x) || Number.isNaN(y)) pen = false;
    else if (pen) ctx.lineTo(x, y);
    else {
      ctx.moveTo(x, y);
      pen = true;
    }
  }
  ctx.stroke();
}

function drawCurves(ctx: CurveCanvas, frame: CurveFrame, style: CurveStyle): void {
  frame.scene.lines.forEach((line, t) => {
    ctx.strokeStyle = colour(style, t);
    ctx.lineWidth = t === frame.activeTrack ? 2 : 1;
    strokeLine(ctx, line);
  });
}

/** A handle: its stem from the key it hangs on, then its disc. */
function drawHandle(ctx: CurveCanvas, frame: CurveFrame, i: number, style: CurveStyle): void {
  const p = frame.scene.points;
  const [x, y] = [p.x[i] ?? 0, p.y[i] ?? 0];
  ctx.strokeStyle = style.selected;
  ctx.beginPath();
  ctx.moveTo(p.ax[i] ?? x, p.ay[i] ?? y);
  ctx.lineTo(x, y);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, CURVE_GRAPH.handlePx, 0, 2 * Math.PI);
  ctx.fill();
}

/** Handles, then key squares; selected keys and every handle in the selection colour, other keys in their track's. */
function drawPoints(ctx: CurveCanvas, frame: CurveFrame, style: CurveStyle): void {
  const p = frame.scene.points;
  const k = CURVE_GRAPH.keyPx;
  ctx.lineWidth = 1;
  p.kind.forEach((kind, i) => {
    ctx.fillStyle = p.selected[i] === 1 ? style.selected : colour(style, p.track[i] ?? 0);
    if (kind === CURVE_POINT.handle) drawHandle(ctx, frame, i, style);
    if (kind === CURVE_POINT.key) ctx.fillRect((p.x[i] ?? 0) - k, (p.y[i] ?? 0) - k, 2 * k, 2 * k);
  });
}

function drawOverlays(ctx: CurveCanvas, frame: CurveFrame, style: CurveStyle): void {
  const x = Math.round(timelineScale.frameToX(frame.axes.time, frame.playhead)) + 0.5;
  ctx.strokeStyle = style.playhead;
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, frame.height);
  ctx.stroke();
  if (frame.box === null) return;
  const r = normalizeRect(frame.box);
  ctx.strokeStyle = style.selected;
  ctx.setLineDash([4, 3]);
  ctx.strokeRect(r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0);
  ctx.setLineDash([]);
}

/** Paints the whole frame in CSS pixels (the caller scales the context for the device pixel ratio). */
export function drawCurveGraph(ctx: CurveCanvas, frame: CurveFrame, style: CurveStyle): void {
  drawCurveGrid(ctx, frame, style);
  drawCurves(ctx, frame, style);
  drawPoints(ctx, frame, style);
  drawOverlays(ctx, frame, style);
}
